import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, CheckCircle, XCircle, Users, Clock, UserX, ShieldX, Link2Off, LogIn } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useAit } from '../context/AitContext';
import { apiPost } from '../utils/api';
import toast from 'react-hot-toast';
import { API_URL } from '../utils/config';
import { getToken as getAuthToken } from '../stores/authStore';

const AcceptAitInvitation = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { fetchProperties } = useAit();

  const [status, setStatus] = useState('loading');
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (token) loadInvitation();
  }, [token]);

  useEffect(() => {
    const shouldAutoAccept = localStorage.getItem('auto_accept_ait_invitation');
    if (shouldAutoAccept === token && status === 'ready') {
      localStorage.removeItem('auto_accept_ait_invitation');
      localStorage.removeItem('jpro_login_redirect');
      localStorage.removeItem('jpro_invite_flag_ts');
      handleAccept();
    }
  }, [status, token]);

  const loadInvitation = async () => {
    try {
      const response = await fetch(`${API_URL}/ait_invitations/${token}`);
      if (!response.ok) {
        setStatus('error');
        const data = await response.json();
        setError(data.error || 'Invalid or expired invitation');
        return;
      }
      const data = await response.json();
      setInvitation(data.invitation);
      setStatus('ready');
    } catch (err) {
      setStatus('error');
      setError('Failed to load invitation');
    }
  };

  const handleAccept = async () => {
    const jwtToken = getAuthToken();
    if (!jwtToken) {
      localStorage.setItem('auto_accept_ait_invitation', token);
      localStorage.setItem('jpro_login_redirect', `/ait-invitations/${token}`);
      localStorage.setItem('jpro_invite_flag_ts', Date.now().toString());
      navigate('/login', { state: { from: { pathname: `/ait-invitations/${token}` } } });
      return;
    }

    setStatus('accepting');
    try {
      const response = await apiPost(`/ait_invitations/${token}/accept`);
      if (response.ok) {
        const data = await response.json();
        setStatus('success');
        toast.success(data.message || 'Successfully joined property!');
        await fetchProperties();
        setTimeout(() => {
          navigate(`/dashboard/ait/${data.property.id}`);
        }, 1000);
      } else {
        const data = await response.json();
        setStatus('error');
        setError(data.error || 'Failed to accept invitation');
      }
    } catch (err) {
      setStatus('error');
      setError('Failed to accept invitation');
    }
  };

  const handleDecline = () => {
    navigate('/dashboard');
  };

  const isDark = theme === 'dark';

  if (status === 'loading') {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="text-center">
          <Loader2 className={`w-12 h-12 animate-spin mx-auto mb-4 ${isDark ? 'text-blue-400' : 'text-blue-500'}`} />
          <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>Loading invitation...</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    const errorLower = error.toLowerCase();
    let ErrorIcon = XCircle;
    let errorTitle = 'Invitation Error';
    let errorHint = null;

    if (errorLower.includes('expired')) {
      ErrorIcon = Clock;
      errorTitle = 'Invitation Expired';
      errorHint = 'Ask the property owner to send a new invitation.';
    } else if (errorLower.includes('already') && errorLower.includes('accepted')) {
      ErrorIcon = CheckCircle;
      errorTitle = 'Already Accepted';
      errorHint = 'You may already be a member of this property.';
    } else if (errorLower.includes('declined')) {
      ErrorIcon = UserX;
      errorTitle = 'Invitation Declined';
      errorHint = 'This invitation was previously declined. Request a new one if needed.';
    } else if (errorLower.includes('revoked') || errorLower.includes('cancelled')) {
      ErrorIcon = ShieldX;
      errorTitle = 'Invitation Revoked';
      errorHint = 'The property owner cancelled this invitation.';
    } else if (errorLower.includes('locked') || errorLower.includes('attempts')) {
      ErrorIcon = ShieldX;
      errorTitle = 'Invitation Locked';
      errorHint = 'Too many attempts. Contact the property owner for a new link.';
    } else if (errorLower.includes('login') || errorLower.includes('sign in') || errorLower.includes('authenticate')) {
      ErrorIcon = LogIn;
      errorTitle = 'Login Required';
      errorHint = 'Please log in first, then try the invitation link again.';
    } else if (errorLower.includes('different') || errorLower.includes('wrong') || errorLower.includes('another account')) {
      ErrorIcon = UserX;
      errorTitle = 'Wrong Account';
      errorHint = 'This invitation was sent to a different email address.';
    } else if (errorLower.includes('invalid') || errorLower.includes('not found')) {
      ErrorIcon = Link2Off;
      errorTitle = 'Invalid Link';
      errorHint = 'This invitation link is not valid. Check that you have the correct URL.';
    }

    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="text-center max-w-md mx-auto p-6">
          <ErrorIcon className={`w-16 h-16 mx-auto mb-4 ${ErrorIcon === CheckCircle ? 'text-blue-500' : 'text-red-500'}`} />
          <h1 className={`text-2xl font-dm-serif mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {errorTitle}
          </h1>
          <p className={`mb-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{error}</p>
          {errorHint && (
            <p className={`text-sm mb-6 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{errorHint}</p>
          )}
          <button
            onClick={() => navigate('/dashboard')}
            className="px-6 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-semibold transition-colors"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="text-center max-w-md mx-auto p-6">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className={`text-2xl font-dm-serif mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Welcome!
          </h1>
          <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>Redirecting you to the property...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen flex items-center justify-center p-4 ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <div className={`w-full max-w-md rounded-lg shadow-xl p-8 ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="text-center mb-6">
          <div className={`w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center ${
            isDark ? 'bg-blue-900/30' : 'bg-blue-100'
          }`}>
            <Users className={`w-8 h-8 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
          </div>
          <h1 className={`text-2xl font-dm-serif mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            You're Invited!
          </h1>
        </div>

        <div className="mb-6">
          <p className={`text-center mb-4 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            <strong>{invitation.invited_by}</strong> invited you to join:
          </p>

          <div className={`p-4 rounded-lg ${
            isDark ? 'bg-blue-900/20 border border-blue-800' : 'bg-blue-50 border border-blue-200'
          }`}>
            <h2 className={`font-dm-serif text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {invitation.property_name}
            </h2>
          </div>
        </div>

        <div className={`mb-6 p-4 rounded-lg ${isDark ? 'bg-gray-750' : 'bg-gray-50'}`}>
          <p className={`text-sm mb-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            You'll join as a <strong className={isDark ? 'text-white' : 'text-gray-900'}>
              {invitation.role}
            </strong>
          </p>
          <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            Expires: {new Date(invitation.expires_at).toLocaleDateString()}
          </p>
        </div>

        <button
          onClick={handleAccept}
          disabled={status === 'accepting'}
          className={`w-full py-3 rounded-lg font-semibold mb-3 transition-colors ${
            status === 'accepting'
              ? 'bg-gray-400 cursor-not-allowed text-white'
              : 'bg-blue-500 hover:bg-blue-600 text-white'
          }`}
        >
          {status === 'accepting' ? 'Accepting...' : 'Accept Invitation'}
        </button>

        <button
          onClick={handleDecline}
          className={`w-full py-2 rounded-lg transition-colors ${
            isDark ? 'text-gray-400 hover:text-gray-300 hover:bg-gray-750' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
          }`}
        >
          Decline
        </button>
      </div>
    </div>
  );
};

export default AcceptAitInvitation;
